import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import add from '../../helpers/add.ts';
import sub from '../../helpers/sub.ts';

interface PaginationSignature {
  Element: HTMLDivElement;
  Args: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
}

function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

function gt(a: number, b: number): boolean {
  return a > b;
}

export default class Pagination extends Component<PaginationSignature> {
  get totalPages(): number {
    return Math.ceil(this.args.total / this.args.pageSize);
  }

  get visiblePages(): number[] {
    const current = this.args.page;
    const total = this.totalPages;
    const pages: number[] = [];

    if (total <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current <= 3) {
        pages.push(2, 3, 4);
        pages.push(-1); // ellipsis
        pages.push(total);
      } else if (current >= total - 2) {
        pages.push(-1); // ellipsis
        pages.push(total - 3, total - 2, total - 1, total);
      } else {
        pages.push(-1); // ellipsis
        pages.push(current - 1, current, current + 1);
        pages.push(-2); // ellipsis
        pages.push(total);
      }
    }

    return pages;
  }

  <template>
    {{#if (gt this.totalPages 1)}}
      <nav aria-label="Image pagination">
        <ul class="pagination pagination-sm mb-0">
          {{! Previous button }}
          <li class="page-item {{if (eq @page 1) 'disabled' ''}}">
            <button
              type="button"
              class="page-link"
              disabled={{eq @page 1}}
              aria-label="Previous page"
              {{on "click" (fn @onPageChange (sub @page 1))}}
            >
              Previous
            </button>
          </li>

          {{! Page numbers with ellipsis }}
          {{#each this.visiblePages as |pageNum|}}
            {{#if (eq pageNum -1)}}
              <li class="page-item disabled">
                <span class="page-link">…</span>
              </li>
            {{else if (eq pageNum -2)}}
              <li class="page-item disabled">
                <span class="page-link">…</span>
              </li>
            {{else}}
              <li class="page-item {{if (eq @page pageNum) 'active' ''}}">
                <button
                  type="button"
                  class="page-link"
                  aria-label="Page {{pageNum}}"
                  {{on "click" (fn @onPageChange pageNum)}}
                >
                  {{pageNum}}
                </button>
              </li>
            {{/if}}
          {{/each}}

          {{! Next button }}
          <li class="page-item {{if (eq @page this.totalPages) 'disabled' ''}}">
            <button
              type="button"
              class="page-link"
              disabled={{eq @page this.totalPages}}
              aria-label="Next page"
              {{on "click" (fn @onPageChange (add @page 1))}}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    {{/if}}
  </template>
}
